import { type FC, type ReactNode, useRef } from "react";

export type {
  DetailAccessorKey,
  DetailBooleanField,
  DetailCardConfig,
  DetailCardsConfig,
  DetailEmailField,
  DetailCustomField,
  DetailDateField,
  DetailFieldAction,
  DetailFieldBase,
  DetailFieldConfig,
  DetailFieldDisplayType,
  DetailFieldGroup,
  DetailFieldGroupMap,
  DetailLinkField,
  DetailStatusField,
  DetailTextField,
  DetailVisibility,
} from "./detail-cards-config.types";
export {
  DETAIL_ACTION_DEFAULT_CONFIRM_MESSAGE,
  DETAIL_ACTION_DROPDOWN_PLACEHOLDER,
} from "./detail-cards-config.types";
export {
  buildRelatedCompanyFieldGroup,
  buildCreatedByFieldGroup,
  createdByEntityLabel,
} from "./field-groups";
import styled, { css } from "styled-components";
import { Copy } from "../../../../components/icons";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import { copyTextToClipboard } from "../../../../util/clipboard";

export const DetailCardsGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ $columns = 2 }) => $columns},
    minmax(0, 1fr)
  );
  gap: 20px;
  margin: 15px 0 20px;
  align-items: stretch;

  @media (max-width: 1300px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 767px) {
    gap: 12px;
    margin: 12px 0 16px;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: visible;
`;

const CardHeader = styled.div`
  padding: 14px 20px 12px;
  border-bottom: 1px solid #e8e8e8;
  @media (max-width: 767px) {
    padding: 12px 14px 10px;
  }
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CardTitle = styled(Typography)`
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.055em;
  text-transform: uppercase;
  line-height: 1.25;
  color: #3d4754;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 20px 16px;
  @media (max-width: 767px) {
    padding: 0 14px 12px;
  }
`;

const Row = styled.div.attrs({ "data-detail-row": "" })`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(200px, 1.2fr);
  gap: 14px 16px;
  align-items: start;
  padding: 12px 0;
  border-bottom: 1px solid #ececec;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 4px;
    padding: 8px 0;
  }
`;

const RowLabel = styled(Typography).attrs({ "data-detail-label": "" })`
  min-width: 0;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  line-height: 1.35;
`;

const RowValue = styled.div.attrs({ "data-detail-value": "" })`
  min-width: 0;
  text-align: right;
  font-size: 14px;
  line-height: 1.35;
  color: #111;
  word-break: break-word;

  a {
    color: #027aff;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }

  @media (max-width: 767px) {
    text-align: left;
  }
`;

/** Shared look for the small buttons in a card header (Edit, Copy). */
const cardHeaderButton = css`
  flex-shrink: 0;
  height: 28px;
  min-height: 28px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fff;
  color: #3d4754;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;

  &:hover {
    background: #f3f4f6;
    border-color: #d6d9de;
    box-shadow: none;
  }
`;

export const CardEditButton = styled(Button).attrs({
  variant: ButtonVariant.OUTLINED,
})`
  ${cardHeaderButton}
  padding: 0 12px;
`;

const CardHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CardCopyButton = styled.button`
  ${cardHeaderButton}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  padding: 0;
`;

/** "Title\nLabel: value\n..." built from the rendered rows so it matches what the user sees. */
function buildCardCopyText(title: string, body: HTMLElement): string {
  const rows = Array.from(
    body.querySelectorAll<HTMLElement>("[data-detail-row]")
  ).map((row) => {
    const label =
      row.querySelector<HTMLElement>("[data-detail-label]")?.innerText.trim() ??
      "";
    const value =
      row.querySelector<HTMLElement>("[data-detail-value]")?.innerText.trim() ||
      "-";
    return `${label}: ${value}`;
  });
  return [title, ...rows].join("\n");
}

export const DetailCard: FC<{
  title: string;
  children: ReactNode;
  headerAction?: ReactNode;
}> = ({ title, children, headerAction }) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const { showNotification } = useNotification();

  const handleCopy = async () => {
    if (!bodyRef.current) return;
    const ok = await copyTextToClipboard(
      buildCardCopyText(title, bodyRef.current)
    );
    showNotification(
      ok ? "Card details copied to clipboard" : "Could not copy card details",
      ok ? NotificationTypes.SUCCESS : NotificationTypes.WARNING
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardHeaderRow>
          <CardTitle variant={TypographyVariant.BODY5}>{title}</CardTitle>
          <CardHeaderActions>
            {headerAction}
            <CardCopyButton
              type="button"
              onClick={handleCopy}
              aria-label="Copy card details"
              title="Copy card details"
            >
              <Copy />
            </CardCopyButton>
          </CardHeaderActions>
        </CardHeaderRow>
      </CardHeader>
      <CardBody ref={bodyRef}>{children}</CardBody>
    </Card>
  );
};

export const DetailCardRow: FC<{
  label: string;
  value?: ReactNode;
}> = ({ label, value }) => {
  const empty =
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "");
  return (
    <Row>
      <RowLabel variant={TypographyVariant.BODY5}>{label}</RowLabel>
      <RowValue>
        {empty ? (
          <Typography variant={TypographyVariant.BODY2}>-</Typography>
        ) : (
          value
        )}
      </RowValue>
    </Row>
  );
};

const StatusLineRow = styled.div.attrs({ "data-detail-row": "" })`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 20px;
  width: 100%;
  min-width: 0;
  padding: 12px 0;
  border-bottom: 1px solid #ececec;

  &:last-child {
    border-bottom: none;
  }
`;

const StatusLineLabel = styled(Typography).attrs({
  "data-detail-label": "",
})`
  flex: 1 1 auto;
  min-width: 140px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  line-height: 1.35;
`;

const StatusLineBadge = styled.div.attrs({ "data-detail-value": "" })`
  flex: 0 0 auto;
  align-self: center;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
`;

const StatusLineActions = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;

  @media (max-width: 767px) {
    margin-left: 0;
  }
`;

/** One row: label — status — (space) — action buttons. */
export const DetailCardStatusRow: FC<{
  label: string;
  status: ReactNode;
  actions?: ReactNode;
}> = ({ label, status, actions }) => (
  <StatusLineRow>
    <StatusLineLabel variant={TypographyVariant.BODY5}>{label}</StatusLineLabel>
    {(status != null || actions == null) && (
      <StatusLineBadge
        style={status == null ? { marginLeft: "auto" } : undefined}
      >
        {status ?? <Typography variant={TypographyVariant.BODY2}>-</Typography>}
      </StatusLineBadge>
    )}
    {actions != null ? <StatusLineActions>{actions}</StatusLineActions> : null}
  </StatusLineRow>
);

export function externalHrefLabel(
  url: string | null | undefined
): ReactNode | undefined {
  if (url == null || String(url).trim() === "") return undefined;
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {url}
    </a>
  );
}

export interface DetailCardFieldConfig {
  label: string;
  value: ReactNode;
}

export interface ConfigDetailCardProps {
  title: string;
  fields: DetailCardFieldConfig[];
  editable?: boolean;
  onEdit?: () => void;
  /** Extra rows appended after the field rows (e.g. DetailCardStatusRow) for cases a plain field config can't express. */
  children?: ReactNode;
}

/** Config-driven wrapper over DetailCard/DetailCardRow. Use DetailCard/DetailCardRow directly for fully custom layouts. */
export const ConfigDetailCard: FC<ConfigDetailCardProps> = ({
  title,
  fields,
  editable,
  onEdit,
  children,
}) => (
  <DetailCard
    title={title}
    headerAction={
      editable && onEdit ? (
        <CardEditButton onClick={onEdit}>Edit</CardEditButton>
      ) : undefined
    }
  >
    {fields.map((field) => (
      <DetailCardRow
        key={field.label}
        label={field.label}
        value={field.value}
      />
    ))}
    {children}
  </DetailCard>
);
